import React, { FunctionComponent, useCallback, useEffect } from "react";
import { Field, useForm, useFormState } from "react-final-form";
import { Grid, makeStyles } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import Autocomplete from "../../../components/Autocomplete";
import useBroadcastQueries from "../hooks/useBroadcastQueries";
import { useQuery } from "react-query";
import Button from "../../../components/Button";

const useStyles = makeStyles(() => ({
  root: {
    padding: "24px 16px",
    borderRadius: 15,
    backgroundColor: "#ffffff",
    boxShadow: "0 0 8px #C2C9D682",
    top: 119,
    position: "sticky",
  },
  container: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
  },
  title: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#231F20",
    lineHeight: "29px",
  },
  mainContainer: {
    marginTop: 12,
    "& .buttonContainer": {
      paddingTop: 18,
    },
  },
  formControl: {
    marginTop: 26,
  },
}));

const FilterForm: FunctionComponent = () => {
  const classes = useStyles();
  const { values } = useFormState();
  const { change } = useForm();
  const {
    fetchSubscribers,
    fetchStudents,
    fetchAgents,
    fetchParents,
    fetchSchoolUsers,
    fetchServiceProviderUsers,
    fetchTeachers,
  } = useBroadcastQueries();

  const {
    isLoading: isLoadingSubscribers,
    isFetching: isFetchingSubscribers,
    data: subscribers,
  } = useQuery("subscribersForBroadcast", async () => {
    const response = await fetchSubscribers();
    return response.data;
  });

  const {
    isLoading: isLoadingStudents,
    isFetching: isFetchingStudents,
    data: students,
  } = useQuery("studentsForBroadcast", async () => {
    const response = await fetchStudents();
    return response.data;
  });

  const {
    isLoading: isLoadingAgents,
    isFetching: isFetchingAgents,
    data: agents,
  } = useQuery("agentsForBroadcast", async () => {
    const response = await fetchAgents();
    return response.data;
  });

  const {
    isLoading: isLoadingParents,
    isFetching: isFetchingParents,
    data: parents,
  } = useQuery("parentsForBroadcast", async () => {
    const response = await fetchParents();
    return response.data;
  });

  const {
    isLoading: isLoadingSchoolUsers,
    isFetching: isFetchingSchoolUsers,
    data: schoolUsers,
  } = useQuery("schoolUsersForBroadcast", async () => {
    const response = await fetchSchoolUsers();
    return response.data;
  });

  const {
    isLoading: isLoadingServiceProviderUsers,
    isFetching: isFetchingServiceProviderUsers,
    data: serviceProviderUsers,
  } = useQuery("serviceProviderUsersForBroadcast", async () => {
    const response = await fetchServiceProviderUsers();
    return response.data;
  });

  const {
    isLoading: isLoadingTeachers,
    isFetching: isFetchingTeachers,
    data: teachers,
  } = useQuery("teachersForBroadcast", async () => {
    const response = await fetchTeachers();
    return response.data;
  });

  const selectAllSubscribers = useCallback(() => {
    if (subscribers?.length) {
      change("subscribers", subscribers);
    }
  }, [change, subscribers]);

  const selectAllStudents = useCallback(() => {
    if (students?.length) {
      change("students", students);
    }
  }, [change, students]);

  const selectAllParents = useCallback(() => {
    if (parents?.length) {
      change("parents", parents);
    }
  }, [change, parents]);

  const selectAllAgents = useCallback(() => {
    if (agents?.length) {
      change("agents", agents);
    }
  }, [change, agents]);

  const selectAllSchoolUsers = useCallback(() => {
    if (schoolUsers?.length) {
      change("schoolUsers", schoolUsers);
    }
  }, [change, schoolUsers]);

  const selectAllServiceProviderUsers = useCallback(() => {
    if (serviceProviderUsers?.length) {
      change("serviceProviderUsers", serviceProviderUsers);
    }
  }, [change, serviceProviderUsers]);

  const selectAllTeachers = useCallback(() => {
    if (teachers?.length) {
      change("teachers", teachers);
    }
  }, [change, teachers]);

  return (
    <div className={classes.root}>
      <div className={classes.container}>
        <Typography className={classes.title} component={"h2"}>
          Choose Users
        </Typography>
      </div>
      <div className={classes.mainContainer}>
        <div>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="subscribers"
                label={`Subscribers (${
                  values?.subscribers?.length || 0
                } selected)`}
                placeholder="Please select subscribers"
                disableCloseOnSelect
                options={subscribers}
                limitTags={4}
                loading={(
                  isLoadingSubscribers || isFetchingSubscribers
                ).toString()}
                disabled={isLoadingSubscribers || isFetchingSubscribers}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllSubscribers}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="students"
                label={`Students (${values?.students?.length || 0} selected)`}
                placeholder="Please select students"
                disableCloseOnSelect
                options={students}
                limitTags={4}
                loading={(isLoadingStudents || isFetchingStudents).toString()}
                disabled={isLoadingStudents || isFetchingStudents}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllStudents}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="parents"
                label={`Parents (${values?.parents?.length || 0} selected)`}
                placeholder="Please select parents"
                disableCloseOnSelect
                options={parents}
                limitTags={4}
                loading={(isLoadingParents || isFetchingParents).toString()}
                disabled={isLoadingParents || isFetchingParents}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllParents}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="agents"
                label={`Agents (${values?.agents?.length || 0} selected)`}
                placeholder="Please select agents"
                disableCloseOnSelect
                options={agents}
                limitTags={4}
                loading={(isLoadingAgents || isFetchingAgents).toString()}
                disabled={isLoadingAgents || isFetchingAgents}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllAgents}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="schoolUsers"
                label={`School Users (${
                  values?.schoolUsers?.length || 0
                } selected)`}
                placeholder="Please select school users"
                disableCloseOnSelect
                options={schoolUsers}
                limitTags={4}
                loading={(
                  isLoadingSchoolUsers || isFetchingSchoolUsers
                ).toString()}
                disabled={isLoadingSchoolUsers || isFetchingSchoolUsers}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllSchoolUsers}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="serviceProviderUsers"
                label={`Service Provider Users (${
                  values?.serviceProviderUsers?.length || 0
                } selected)`}
                placeholder="Please select service provider users"
                disableCloseOnSelect
                options={serviceProviderUsers}
                limitTags={4}
                loading={(
                  isLoadingServiceProviderUsers ||
                  isFetchingServiceProviderUsers
                ).toString()}
                disabled={
                  isLoadingServiceProviderUsers ||
                  isFetchingServiceProviderUsers
                }
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllServiceProviderUsers}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
        <div className={classes.formControl}>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={10}>
              <Field
                fullWidth
                multiple={true}
                name="teachers"
                label={`Teachers (${values?.teachers?.length || 0} selected)`}
                placeholder="Please select teachers"
                disableCloseOnSelect
                options={teachers}
                limitTags={4}
                loading={(
                  isLoadingTeachers || isFetchingTeachers
                ).toString()}
                disabled={isLoadingTeachers || isFetchingTeachers}
                component={Autocomplete}
              />
            </Grid>
            <Grid item xs={2} className="buttonContainer">
              <Button
                type="button"
                color="secondary"
                variant="contained"
                fullWidth
                onClick={selectAllTeachers}
              >
                Select All
              </Button>
            </Grid>
          </Grid>
        </div>
      </div>
    </div>
  );
};

export default React.memo(FilterForm);
